<template>
    <div id="edit-whatsapp-page">
        <ChannelForm
            ref="channelForm"
            :steps="formSteps"
            :current-step="currentStep"
            :connection-status="connectionStatus"
            @update:currentStep="setStep"
            @step-change="onStepChange"
        >
            <template #default="{ nextStep, previousStep, close }">
                <!-- Configure Step -->
                <StepConfigure
                    v-if="currentStep === 'configure'"
                    :sender="sender"
                    :place-sender="place_sender"
                    channel="whatsapp"
                    @saved="onConfigureSaved"
                    @cancel="close"
                />

                <!-- Scan Step (WhatsApp specific) -->
                <div class="col-12 mt-4" v-if="currentStep === 'scan'">
                    <div class="d-flex justify-content-center">
                        <div class="box box-secondary text-center qr-card">
                            <h6 v-if="connectionStatus === 'error'" class="text-danger">
                                {{ t('settings-connections-steps-connection_check_error') }} <br />
                                <small>{{ t('settings-connections-steps-verify_settings_or_support') }}</small>
                            </h6>
                            <h6 v-else-if="qrCodeStatus !== 'failed' && connectionStatus !== 'connected'">{{ t('settings-connections-steps-scan_qr_instruction') }}</h6>
                            <h6 v-else-if="qrCodeStatus === 'failed' && connectionStatus !== 'connected'" class="text-danger">
                                {{ t('settings-connections-steps-qr_code_error') }} <br />
                                <small>{{ t('settings-connections-steps-verify_settings_or_support') }}</small>
                            </h6>
                            <h6 v-else-if="connectionStatus === 'connected'" class="text-success">{{ t('settings-connections-steps-whatsapp_connected') }}</h6>
                            <div class="qr-code-container d-flex justify-content-center align-items-center">
                                <loader inline v-if="qrCodeStatus === 'loading' && connectionStatus !== 'connected'" />
                                <img
                                    :src="qrCode"
                                    v-if="qrCodeStatus === 'generated' && connectionStatus !== 'connected'"
                                    :title="t('settings-connections-steps-expand_qr')"
                                    @click="showExpandedQr = true"
                                />
                                <div v-if="qrCodeStatus === 'failed' && connectionStatus !== 'connected'" class="text-danger">
                                    <Icon icon-class="ui-alert-circle" size="10" />
                                </div>
                                <div v-if="connectionStatus === 'connected'" class="text-success">
                                    <Icon icon-class="ui-check-circle" size="10" />
                                </div>
                            </div>
                            <div v-if="qrCodeStatus === 'generated' && connectionStatus !== 'connected'" class="mt-2 d-flex justify-content-center">
                                <KvButton
                                    variant="tonal"
                                    size="small"
                                    prepend-icon="ui-maximize"
                                    @click="showExpandedQr = true"
                                >
                                    {{ t('settings-connections-steps-expand_qr') }}
                                </KvButton>
                            </div>
                            <div v-if="qrCodeAttempts >= 6 && connectionStatus !== 'connected'" class="mt-3">
                                <button class="btn btn-primary" @click="resetAndGenerateQrCode">
                                    {{ t('settings-connections-steps-generate_qr_again') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="action-buttons mt-4">
                        <button class="btn btn-secondary" @click="close">{{ t('settings-connections-steps-cancel') }}</button>
                        <button v-if="qrCodeStatus === 'failed' && connectionStatus !== 'connected'" class="btn btn-primary" @click="resetAndGenerateQrCode">
                            {{ t('settings-connections-steps-try_again') }}
                        </button>
                        <button class="btn btn-success" @click="nextStep" :disabled="connectionStatus !== 'connected'">
                            <Icon icon-class="uiarrow-narrow-right" size="4" />
                            <span>{{ t('settings-connections-steps-next') }}</span>
                        </button>
                    </div>
                </div>

                <!-- Test Step -->
                <StepTestPhone
                    v-if="currentStep === 'test'"
                    ref="stepTest"
                    :input-label="t('settings-connections-steps-enter_mobile_short')"
                    input-placeholder="(##) #####-####"
                    input-id="test-whatsapp"
                    :testing="testing"
                    @test="testConnection"
                    @back="previousStep"
                />

                <!-- Finished Step -->
                <StepFinished
                    v-if="currentStep === 'finished'"
                    channel="whatsapp"
                    @back="previousStep"
                    @retry-test="setStep('test')"
                    @done="$emit('done')"
                />
            </template>
        </ChannelForm>

        <KvModal
            size="small"
            :modelValue="showExpandedQr"
            @update:modelValue="showExpandedQr = $event"
        >
            <template #title>
                <span>{{ t('settings-connections-steps-expanded_qr_title') }}</span>
            </template>
            <template #subtitle>
                <span>{{ t('settings-connections-steps-keep_phone_close') }}</span>
            </template>
            <template #body>
                <div class="d-flex flex-column align-items-center justify-content-center py-2">
                    <div class="expanded-qr-wrapper d-flex align-items-center justify-content-center">
                        <loader inline v-if="qrCodeStatus === 'loading' && connectionStatus !== 'connected'" />
                        <img
                            :src="qrCode"
                            v-if="qrCodeStatus === 'generated' && connectionStatus !== 'connected'"
                            class="expanded-qr-img"
                        />
                        <div v-if="qrCodeStatus === 'failed' && connectionStatus !== 'connected'" class="text-danger text-center">
                            <Icon icon-class="ui-alert-circle" size="10" />
                            <p class="mt-2 text-danger">{{ t('settings-connections-steps-qr_code_error') }}</p>
                        </div>
                        <div v-if="connectionStatus === 'connected'" class="text-success text-center">
                            <Icon icon-class="ui-check-circle" size="10" />
                            <h5 class="mt-2 text-success">{{ t('settings-connections-steps-whatsapp_connected') }}</h5>
                        </div>
                    </div>
                    <div v-if="qrCodeAttempts >= 6 && connectionStatus !== 'connected'" class="mt-3">
                        <button class="btn btn-primary" @click="resetAndGenerateQrCode">
                            {{ t('settings-connections-steps-generate_qr_again') }}
                        </button>
                    </div>
                </div>
            </template>
            <template #footer="{ toggle }">
                <KvButton variant="outlined" @click="toggle">{{ t('settings-connections-steps-close') }}</KvButton>
            </template>
        </KvModal>
    </div>
</template>

<script>
import keviApi from '../../../keviApi';
import ChannelForm from './channel-form.vue';
import StepConfigure from './steps/step-configure.vue';
import StepTestPhone from './steps/step-test-phone.vue';
import StepFinished from './steps/step-finished.vue';
import KvModal from '@/components/modal/modal.vue';
import KvButton from '@/components/form/kv-button.vue';

export default {
    name: 'edit-whatsapp-page',
    components: {
        ChannelForm,
        StepConfigure,
        StepTestPhone,
        StepFinished,
        KvModal,
        KvButton,
    },
    props: {
        sender: {
            type: Object,
            required: true,
        },
        place_sender: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            currentStep: 'configure',
            qrCode: null,
            qrCodeStatus: 'loading',
            connectionStatus: 'loading',
            intervalConnections: null,
            qrCodeTimeout: null,
            qrCodeAttempts: 0,
            testing: false,
            showExpandedQr: false,
        };
    },
    computed: {
        formSteps() {
            return [
                { id: 'configure', label: this.t('settings-connections-steps-configure'), icon: 'ui-settings-02' },
                { 
                    id: 'scan', 
                    label: this.t('settings-connections-steps-scan'), 
                    icon: 'ui-qr-code',
                    disabled: !this.place_sender.id,
                    requiresConnection: true,
                },
                { 
                    id: 'test', 
                    label: this.t('settings-connections-steps-test'), 
                    icon: 'ui-loading-01',
                    disabled: !this.place_sender.id || this.connectionStatus !== 'connected',
                },
                { 
                    id: 'finished', 
                    label: this.t('settings-connections-steps-finish'), 
                    icon: 'ui-check-circle',
                    disabled: !this.place_sender.id || this.connectionStatus !== 'connected',
                },
            ];
        },
    },
    methods: {
        checkConnectionStatus(generateQrCode = true) {
            var options = keviApi.getOptions("senders/whatsapp/is-connected/" + this.place_sender.id);
            this.$http(options).then((result) => {
                this.connectionStatus = result.data.is_connected ? 'connected' : 'disconnected';
                if(this.connectionStatus !== 'connected' && generateQrCode) {
                    this.generateQrCode();
                }
            }).catch((error) => {
                console.log(error);
                this.connectionStatus = 'error';
                this.qrCodeStatus = 'failed';
            });
        },
        setStep(step) {
            if (['configure', 'scan', 'test', 'finished'].includes(step)) {
                this.currentStep = step;
                if (step !== 'scan') {
                    this.showExpandedQr = false;
                }
            }
        },
        onStepChange(step) {
            if (step === 'scan') {
                this.checkConnectionStatus();
            }
        },
        onConfigureSaved(placeSender) {
            this.$emit('saved', placeSender);
            // Move to next step (scan)
            this.currentStep = 'scan';
        },
        testConnection(phone) {
            this.testing = true;
            var options = keviApi.getOptions("senders/whatsapp/test", "POST", {
                places_senders_id: this.place_sender.id,
                phone_number: phone,
            });
            this.$http(options).then((result) => {
                this.$store.dispatch('notification/success', this.$_('settings-connections-notifications-test_sent_success'));
                this.currentStep = 'finished';
            }, (error) => {
                const channel = this.sender.channel;
                let errorMessage = this.$_(`notifications-errors-${channel}-${error.body.error}`, null, this.$_('settings-connections-notifications-test_send_error', [error.body?.message || ""]));
                this.$store.dispatch('notification/error', errorMessage);
            }).finally(() => {
                this.testing = false;
            });
        },
        resetAndGenerateQrCode() {
            this.qrCodeAttempts = 0;
            this.qrCodeStatus = 'loading';
            this.generateQrCode();
        },
        generateQrCode() {
            if(this.connectionStatus !== 'disconnected') {
                return;
            }
            
            if(this.qrCodeTimeout) {
                window.clearTimeout(this.qrCodeTimeout);
                this.qrCodeTimeout = null;
            }
            
            var options = keviApi.getOptions("senders/whatsapp/qr-code/" + this.place_sender.id);
            this.$http(options).then((result) => {
                if(result.data.qr_code && !result.data.is_connected) {
                    this.qrCode = result.data.qr_code;
                    this.qrCodeStatus = 'generated';
                    this.qrCodeAttempts++;
                    
                    if(this.qrCodeAttempts < 20 && this.connectionStatus !== 'connected') {
                        this.qrCodeTimeout = window.setTimeout(() => {
                            if(this.connectionStatus !== 'connected') {
                                this.generateQrCode();
                            }
                        }, 5000);
                    }
                } else if(result.data.is_connected) {
                    this.connectionStatus = 'connected';
                    this.qrCodeStatus = 'success';
                    this.qrCodeAttempts = 0;
                } else {
                    this.qrCodeStatus = 'failed';
                    this.qrCodeAttempts++;
                    
                    if(this.qrCodeAttempts < 6 && this.connectionStatus !== 'connected') {
                        this.qrCodeTimeout = window.setTimeout(() => {
                            if(this.connectionStatus !== 'connected') {
                                this.generateQrCode();
                            }
                        }, 10000);
                    }
                }
            }).catch((error) => {
                this.qrCodeStatus = 'failed';
                this.qrCodeAttempts++;
                
                if(this.qrCodeAttempts < 6 && this.connectionStatus !== 'connected') {
                    this.qrCodeTimeout = window.setTimeout(() => {
                        if(this.connectionStatus !== 'connected') {
                            this.generateQrCode();
                        }
                    }, 10000);
                }
            });
        }
    },
    async mounted() {
        if (this.place_sender && this.place_sender.id) {
            this.currentStep = 'scan';
            this.checkConnectionStatus();
        }
    },
    watch: {
        place_sender: {
            async handler() {
                this.qrCode = null;
                this.qrCodeStatus = 'loading';
                this.qrCodeAttempts = 0;
                if(this.qrCodeTimeout) {
                    window.clearTimeout(this.qrCodeTimeout);
                    this.qrCodeTimeout = null;
                }
                if(this.place_sender.id) {
                    this.currentStep = 'scan';
                    this.checkConnectionStatus();
                } else {
                    this.currentStep = 'configure';
                }
            },
            deep: true,
            immediate: true,
        },
        sender: {
            handler() {
                this.qrCode = null;
                this.qrCodeStatus = 'loading';
                this.qrCodeAttempts = 0;
                if(this.qrCodeTimeout) {
                    window.clearTimeout(this.qrCodeTimeout);
                    this.qrCodeTimeout = null;
                }
            },
            deep: true,
            immediate: true,
        },
        connectionStatus() {
            if(this.connectionStatus === 'connected') {
                window.clearInterval(this.intervalConnections);
                this.intervalConnections = null;
                if(this.qrCodeTimeout) {
                    window.clearTimeout(this.qrCodeTimeout);
                    this.qrCodeTimeout = null;
                }
                this.qrCodeAttempts = 0;
                this.showExpandedQr = false;
            }
        },
        qrCodeStatus() {
            if(this.qrCodeStatus === 'generated') {
                if(!this.intervalConnections) {
                    this.intervalConnections = window.setInterval(() => {
                        this.checkConnectionStatus(false);
                    }, 10000);
                }
            } else {
                window.clearInterval(this.intervalConnections);
                this.intervalConnections = null;
            }
        },
    }
}
</script>

<style scoped lang="less">
#edit-whatsapp-page {
    .box {
        padding: 20px;
    }

    .qr-card {
        max-width: 300px;
        width: 100%;
        position: relative;
    }

    .qr-code-container {
        height: 240px;
        width: 240px;
        margin: 0 auto;
        position: relative;
        background: #fff;
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

        img {
            max-width: 100%;
            max-height: 100%;
            image-rendering: pixelated;
            cursor: zoom-in;
            border-radius: 4px;
        }
    }

    .expanded-qr-wrapper {
        min-height: 340px;
        min-width: 340px;
        max-width: 100%;
        background: #fff;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

        .expanded-qr-img {
            width: 340px;
            height: 340px;
            max-width: 100%;
            max-height: 100%;
            image-rendering: pixelated;
            border-radius: 8px;
        }
    }

    .action-buttons {
        display: flex;
        gap: 12px;
        justify-content: stretch;
        
        button {
            flex: 1;
            justify-content: center;
        }
    }
}
</style>
